/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2019, Zumper
 * @description
 */

import { LoadableComponent } from '@loadable/component'
import { useMountAndUnmount } from '@zcode-ui/hook'
import hoistStatics from 'hoist-non-react-statics'
import React, { useState } from 'react'
import { flushSync } from 'react-dom'

export const withReload =
  <Props extends object>({
    loadingFallback: LoadingFallback,
  }: {
    loadingFallback: React.ComponentType<Props>
  }) =>
  (Loadable: LoadableComponent<Props>) => {
    let startLoading: undefined | (() => void)
    let doneLoading: undefined | (() => void)
    let mounted: boolean

    const WithReload = (props: Props) => {
      const [isLoading, setIsLoading] = useState(false)

      useMountAndUnmount({
        onMount: () => {
          mounted = true
        },
        onUnmount: () => {
          startLoading = undefined
          doneLoading = undefined
          mounted = false
        },
      })

      if (!startLoading || !doneLoading) {
        startLoading = () => {
          flushSync(() => {
            mounted && setIsLoading(true)
          })
        }
        doneLoading = () => {
          flushSync(() => {
            mounted && setIsLoading(false)
          })
        }
      }

      return isLoading ? (
        <LoadingFallback {...props} />
      ) : (
        <Loadable {...props} />
      )
    }
    WithReload.reload = async (props?: Props) => {
      startLoading?.()
      await Loadable.load(props)
      doneLoading?.()
    }
    return hoistStatics(WithReload, Loadable)
  }
